import {BRIEFING, confidenceBadge, CONFIDENCE_DIMENSIONS, factsRow} from "./briefingData";
import type {Assumption, Opportunity, DerivationStep, ConfidenceDimension, ScoringCriterion} from "./briefingTypes";

/**
 * Pure recalculation layer. Everything here derives from fixture numbers plus the
 * user's in-session overrides (dismissals, conversions, assumption edits, the
 * simulated fix round-trip). No wall clock: "today" is always the fixture's
 * session date.
 *
 * Every recomputation formula is anchored so that at the fixture's default inputs
 * it reproduces the fixture's displayed numbers exactly — the fixture stays the
 * source of truth at rest, and overrides move the numbers along the same arithmetic
 * the fixture documents (potential_impact chain, formula_registry).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export function parseIsoDay(iso: string): Date {
    return new Date(`${iso}T00:00:00Z`);
}

export function addDays(iso: string, n: number): string {
    return new Date(parseIsoDay(iso).getTime() + n * DAY_MS).toISOString().slice(0, 10);
}

export function daysBetween(fromIso: string, toIso: string): number {
    return Math.round((parseIsoDay(toIso).getTime() - parseIsoDay(fromIso).getTime()) / DAY_MS);
}

export function isWorkingDay(iso: string): boolean {
    const weekday = parseIsoDay(iso).getUTCDay();
    return weekday !== 0 && weekday !== 6;
}

/** Last working day strictly before the given date (fixture rule: 3 Aug Mon → Fri 31 Jul). */
export function lastWorkingDayBefore(iso: string): string {
    let d = addDays(iso, -1);
    while (!isWorkingDay(d)) d = addDays(d, -1);
    return d;
}

/** "2026-07-31" → "31 July" */
export function formatDayMonth(iso: string): string {
    const d = parseIsoDay(iso);
    return `${d.getUTCDate()} ${d.toLocaleDateString('en-GB', {month: 'long', timeZone: 'UTC'})}`;
}

/** Compact euro display in the fixture's own style: €740k, €18.7k, €1.04m, €12.3m */
export function formatCompactEuro(v: number): string {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `€${parseFloat((v / 1_000_000).toFixed(2))}m`;
    if (abs >= 100_000) return `€${Math.round(v / 1_000)}k`;
    return `€${parseFloat((v / 1_000).toFixed(1))}k`;
}

const round1000 = (v: number): number => Math.round(v / 1000) * 1000;

export type ContractWindowTiming = {
    theoreticalIso: string
    startIso: string
    bufferDays: number
    daysToExpiry: number
}

/** Expiry minus cycle, stepped back to the last working day, buffered from today. */
export function deriveContractWindowTiming(expiryIso: string, cycleDays: number, todayIso: string): ContractWindowTiming {
    const theoreticalIso = addDays(expiryIso, -cycleDays);
    const startIso = lastWorkingDayBefore(theoreticalIso);
    return {
        theoreticalIso,
        startIso,
        bufferDays: daysBetween(todayIso, startIso),
        daysToExpiry: daysBetween(todayIso, expiryIso),
    };
}

/** contract_window: value_at_stake × min(1, 7 ÷ buffer_days). The only accelerating type. */
export function contractWindowCostOfWaiting(valueAtStake: number, bufferDays: number): number {
    if (bufferDays <= 0) return valueAtStake;
    return Math.round(valueAtStake * Math.min(1, 7 / bufferDays));
}

/** scope.base_spend x current_activity.addressable_share x future_conditions.continuation_factor x commercial_basis.rate_gap_pct */
export function composeImpact(opportunity: Opportunity, patch?: {baseSpend?: number, rateGapPct?: number}): number {
    const c = opportunity.potential_impact;
    const base = patch?.baseSpend ?? c.base_spend;
    const rate = patch?.rateGapPct ?? c.rate_gap_pct;
    return base * c.addressable_share * c.continuation_factor * (rate / 100);
}

// ---------------------------------------------------------------------------
// Assumptions — the editable layer (PRD: editable vs locked)
// ---------------------------------------------------------------------------

export type AssumptionOverride = {value: number, reason: string};

export function assumptionCatalog(): Array<{opportunityId: string, assumption: Assumption}> {
    return BRIEFING.opportunities.flatMap(c => (c.assumptions ?? []).map(assumption => ({opportunityId: c.id, assumption})));
}

export function findAssumption(id: string): {opportunityId: string, assumption: Assumption} | undefined {
    return assumptionCatalog().find(a => a.assumption.id === id);
}

export function assumptionDefault(id: string): number {
    const found = findAssumption(id);
    if (!found) throw new Error(`Unknown assumption: ${id}`);
    return found.assumption.value;
}

export function formatAssumptionValue(id: string, value: number): string {
    const unit = findAssumption(id)?.assumption.unit ?? '';
    if (unit === '%') return `${value}%`;
    if (unit === 'factor') return `${value}`;
    const singular = value === 1 && unit.endsWith('s') ? unit.slice(0, -1) : unit;
    return `${value} ${singular}`;
}

// ---------------------------------------------------------------------------
// The simulated fix round-trip (supplier merge on the FaciliCore opportunity). Surface numbers come
// verbatim from the fixture's human_in_the_loop.diff_banner_example: spend
// €11.8m → €12.3m, savings €740k → €771k, confidence 87% → 91%. Savings follow
// from the value chain on the new base (12.3m × 1.0 × 0.98 × 6.4% ≈ 771k). The
// post-fix state is scripted as evaluated checklists over the same schema, so
// the dimension confidences stay checklist-derived (91 = MIN under the sum rule).
// ---------------------------------------------------------------------------
export const FIX_OPPORTUNITY_ID = 'faciliCore-ifm-renewal';
export const FIX_FACT_RECORD = 'SUP-88214';
export const FIX_DELTA = {
    baseSpend: 12_300_000,
};

export const FIX_CHECKLISTS: Record<ConfidenceDimension, ScoringCriterion[]> = {
    scope: [ // 100 − 4 = 96
        {id: 'cw-scope-rollup', label: 'Supplier roll-up resolved — 4 aliases, 18 entities', status: 'met', deduction: 0},
        {id: 'cw-scope-corroboration', label: 'Every entity corroborated by two independent sources', status: 'partial', deduction: 4, note: 'Confirmed via VAT registry during the 27 Jul merge; 1 newly merged entity awaiting first invoice cycle'},
        {id: 'cw-scope-classification', label: 'Spend classification human-confirmed', status: 'met', deduction: 0},
    ],
    commercial_basis: [ // 100 − 9 = 91
        {id: 'cw-commercial-contract', label: 'Contract record on file with expiry', status: 'met', deduction: 0},
        {id: 'cw-commercial-policy', label: 'Sourcing policy requirement identified', status: 'met', deduction: 0},
        {id: 'cw-commercial-benchmark', label: 'Internal rate benchmark available', status: 'met', deduction: 0},
        {id: 'cw-commercial-ratecard-fresh', label: 'Rate card current (reviewed within 24 months)', status: 'partial', deduction: 4, note: 'Rate basis recomputed over the merged entities on 27 Jul'},
        {id: 'cw-commercial-benchmark-clause', label: 'Benchmark clause present in contract', status: 'unmet', deduction: 5, note: 'FM-017 carries no benchmark clause'},
    ],
    current_activity: [ // 100 − 7 = 93
        {id: 'cw-activity-history', label: '12-month invoice history complete across ERPs', status: 'met', deduction: 0},
        {id: 'cw-activity-coverage', label: 'Contract coverage at or above 80%', status: 'unmet', deduction: 7, note: 'Coverage recomputed at 64% after the merge'},
        {id: 'cw-activity-links', label: 'Contract-to-spend links confirmed', status: 'met', deduction: 0},
    ],
    future_conditions: [ // 100 − 9 = 91
        {id: 'cw-future-plan', label: 'Demand plan covers the full scope', status: 'met', deduction: 0},
        {id: 'cw-future-sites', label: 'No unresolved site changes', status: 'met', deduction: 0},
        {id: 'cw-future-volume', label: 'Volume forecast confirmed as a point estimate', status: 'partial', deduction: 4, note: 'Band re-checked over the merged scope'},
        {id: 'cw-future-fresh', label: 'Plan re-confirmed within 5 days', status: 'unmet', deduction: 5, note: 'IWMS last synced 20 July'},
    ],
};

/** The deterministic scoring rule: class confidence = 100 − sum of deductions. */
export function checklistConfidence(checklist: ScoringCriterion[]): number {
    return 100 - checklist.reduce((acc, c) => acc + c.deduction, 0);
}

export type BriefingOverrides = {
    dismissed: Record<string, {reason: string, note?: string}>
    createdInitiatives: string[]
    assumptionOverrides: Record<string, AssumptionOverride>
    fixApplied: boolean
}

export const INITIAL_OVERRIDES: BriefingOverrides = {
    dismissed: {},
    createdInitiatives: [],
    assumptionOverrides: {},
    fixApplied: false,
};

export function assumptionValue(overrides: BriefingOverrides, id: string): number {
    return overrides.assumptionOverrides[id]?.value ?? assumptionDefault(id);
}

export type ComputedOpportunity = {
    opportunity: Opportunity
    appliesTo: string
    dimensionConfidences: Record<ConfidenceDimension, number>
    checklists: Record<ConfidenceDimension, ScoringCriterion[]>
    confidence: number
    timingLabel: string
    facts: Array<{slot: number, label: string, display: string, type: string}>
    derivationSteps: DerivationStep[]
    costPerWeek: number
    costDisplay: string
    costPlainLanguage: string
    bufferDays?: number
    impact: number
    impactType: string
}

export function defaultCycleTimeDays(): number {
    return assumptionDefault('cycle_time');
}

function contractExpiryIso(opportunity: Opportunity): string {
    // Step 1 of the derivation carries the expiry date
    return opportunity.timing.derivation.steps[0]?.value ?? BRIEFING.session.today;
}

type TypeCompute = Pick<ComputedOpportunity, 'facts' | 'derivationSteps' | 'costPerWeek' | 'costDisplay' | 'impact'>
    & Partial<Pick<ComputedOpportunity, 'timingLabel' | 'bufferDays'>>;

function baseCompute(opportunity: Opportunity): TypeCompute {
    return {
        facts: factsRow(opportunity).map(f => ({slot: f.slot, label: f.label, display: f.display, type: f.type})),
        derivationSteps: opportunity.timing.derivation.steps,
        costPerWeek: opportunity.cost_of_waiting.amount_per_week,
        costDisplay: opportunity.cost_of_waiting.display,
        impact: opportunity.potential_impact.amount,
    };
}

const patchFact = (facts: TypeCompute['facts'], type: string, display: string) =>
    facts.map(f => f.type === type ? {...f, display} : f);

const patchStep = (steps: DerivationStep[], stepNo: number, value: string) =>
    steps.map(s => s.step === stepNo ? {...s, value, note: undefined} : s);

/** Contract window: cycle time and target rate move the clock and the value. */
function computeContractWindow(opportunity: Opportunity, overrides: BriefingOverrides): TypeCompute {
    const out = baseCompute(opportunity);
    const fixed = overrides.fixApplied && opportunity.id === FIX_OPPORTUNITY_ID;
    const cycle = assumptionValue(overrides, 'cycle_time');
    const rateGapPct = assumptionValue(overrides, 'opportunity_rate');
    const timing = deriveContractWindowTiming(contractExpiryIso(opportunity), cycle, BRIEFING.session.today);
    const c = opportunity.potential_impact;
    const base = fixed ? FIX_DELTA.baseSpend : c.base_spend;

    out.impact = round1000(base * c.addressable_share * c.continuation_factor * (rateGapPct / 100));
    out.bufferDays = timing.bufferDays;
    out.costPerWeek = contractWindowCostOfWaiting(out.impact, timing.bufferDays);
    out.costDisplay = `${formatCompactEuro(out.costPerWeek)}/week`;
    out.timingLabel = `Last responsible start: ${formatDayMonth(timing.startIso)}`;

    // Fixture step notes narrate the default numbers — drop them once the
    // assumption is overridden and the values no longer match.
    const defaultCycle = cycle === assumptionDefault('cycle_time');
    out.derivationSteps = opportunity.timing.derivation.steps.map(step => {
        switch (step.step) {
            case 2:
                return {...step, value: cycle % 7 === 0 ? `${cycle} days (${cycle / 7} weeks)` : `${cycle} days`};
            case 3:
                return {...step, value: timing.theoreticalIso, note: defaultCycle ? step.note : undefined};
            case 4:
                return {...step, value: timing.startIso, note: defaultCycle ? step.note : undefined};
            case 5:
                return {...step, value: `${timing.bufferDays} days`};
            default:
                return step;
        }
    });

    out.facts = patchFact(out.facts, 'savings', formatCompactEuro(out.impact));
    out.facts = patchFact(out.facts, 'timing', `${timing.bufferDays} day${timing.bufferDays === 1 ? '' : 's'}`);
    return out;
}

/** Payment terms: the target term moves the cash release — never the savings total. */
function computePaymentTerms(opportunity: Opportunity, overrides: BriefingOverrides): TypeCompute {
    const out = baseCompute(opportunity);
    const terms = assumptionValue(overrides, 'target_terms');
    if (terms === assumptionDefault('target_terms')) return out; // fixture displays at rest

    const current = opportunity.impact_logic?.current_terms_days ?? 34;
    const dpoShiftDays = Math.max(0, terms - current);
    const c = opportunity.potential_impact;
    out.impact = round1000(c.base_spend * c.addressable_share * c.continuation_factor * (dpoShiftDays / 365));
    const monthly = out.impact / 12;
    out.costPerWeek = Math.round(out.impact / 52);
    out.costDisplay = `${formatCompactEuro(out.costPerWeek)}/week`;
    out.facts = patchFact(out.facts, 'cash_release', formatCompactEuro(out.impact));
    out.facts = patchFact(out.facts, 'timing', `${formatCompactEuro(monthly)}/month`);
    out.derivationSteps = patchStep(patchStep(opportunity.timing.derivation.steps, 1, formatCompactEuro(out.impact)), 2, formatCompactEuro(monthly));
    return out;
}

/**
 * Off-contract leakage: the scope-adjustment factor moves the value AND
 * the commercial confidence — the like-for-like scoring criterion's deduction
 * scales with the adjustment magnitude (see scopeAdjustmentDeduction).
 */
function computeLeakage(opportunity: Opportunity, overrides: BriefingOverrides): TypeCompute {
    const out = baseCompute(opportunity);
    const factor = assumptionValue(overrides, 'scope_adjustment');
    if (factor === assumptionDefault('scope_adjustment')) return out;

    const rawPremium = opportunity.impact_logic?.raw_premium_pct ?? 17.2;
    const premiumPct = rawPremium * factor;
    const c = opportunity.potential_impact;
    out.impact = round1000(c.base_spend * c.addressable_share * c.continuation_factor * (premiumPct / 100));
    out.costPerWeek = Math.round(out.impact / 52);
    out.costDisplay = `${formatCompactEuro(out.costPerWeek)}/week`;
    out.facts = patchFact(out.facts, 'savings', formatCompactEuro(out.impact));
    out.facts = patchFact(out.facts, 'timing', `${formatCompactEuro(out.impact / 12)}/month`);
    let steps = patchStep(opportunity.timing.derivation.steps, 3, `${parseFloat(premiumPct.toFixed(1))}%`);
    steps = patchStep(steps, 4, formatCompactEuro(out.impact));
    out.derivationSteps = patchStep(steps, 5, formatCompactEuro(out.impact / 12));
    return out;
}

/**
 * Fragmentation: the realization lag moves the ranking only — the value
 * is unchanged, its urgency derates with how long it takes to land. Anchored to
 * the fixture pair (13 weeks → €2,692/week = annual ÷ 52).
 */
function computeFragmentation(opportunity: Opportunity, overrides: BriefingOverrides): TypeCompute {
    const out = baseCompute(opportunity);
    const lag = assumptionValue(overrides, 'realization_lag');
    const defaultLag = assumptionDefault('realization_lag');
    if (lag === defaultLag) return out;

    const safeLag = Math.max(1, lag);
    out.costPerWeek = Math.round((opportunity.potential_impact.amount / 52) * (defaultLag / safeLag));
    out.costDisplay = `${formatCompactEuro(out.costPerWeek)}/week`;
    out.facts = patchFact(out.facts, 'timing', `${safeLag} week${safeLag === 1 ? '' : 's'} out`);
    out.derivationSteps = patchStep(opportunity.timing.derivation.steps, 2, `${safeLag} weeks`);
    return out;
}

const COMPUTE_BY_TYPE: Record<string, (opportunity: Opportunity, overrides: BriefingOverrides) => TypeCompute> = {
    contract_window: computeContractWindow,
    payment_terms: computePaymentTerms,
    off_contract_leakage: computeLeakage,
    supplier_fragmentation: computeFragmentation,
};

/**
 * The like-for-like criterion's deduction scales with the scope-adjustment
 * magnitude: round(|1 − factor| × 175), anchored to the fixture pair
 * (0.92 → −14). At factor 1.0 the criterion is genuinely met (0 deduction).
 */
export function scopeAdjustmentDeduction(factor: number): number {
    return Math.round(Math.abs(1 - factor) * 175);
}

/**
 * Evaluated scoring checklists per class: the fixture's evaluation at rest,
 * re-evaluated where an override changes a criterion (scope adjustment) and
 * swapped for the scripted post-merge evaluation after the fix round-trip.
 */
export function opportunityChecklists(opportunity: Opportunity, overrides: BriefingOverrides): Record<ConfidenceDimension, ScoringCriterion[]> {
    if (overrides.fixApplied && opportunity.id === FIX_OPPORTUNITY_ID) return FIX_CHECKLISTS;

    const checklists = Object.fromEntries(CONFIDENCE_DIMENSIONS.map(cls =>
        [cls, opportunity.confidence[cls].scoring?.checklist ?? []],
    )) as Record<ConfidenceDimension, ScoringCriterion[]>;

    if (opportunity.opportunity_type === 'off_contract_leakage') {
        const factor = assumptionValue(overrides, 'scope_adjustment');
        if (factor !== assumptionDefault('scope_adjustment')) {
            const deduction = scopeAdjustmentDeduction(factor);
            checklists.commercial_basis = checklists.commercial_basis.map(c =>
                c.id === 'ocl-commercial-likeforlike'
                    ? {
                        ...c,
                        status: deduction === 0 ? 'met' : 'unmet',
                        deduction,
                        note: deduction === 0
                            ? 'Comparison confirmed like-for-like (factor 1.0)'
                            : `Comparison is scope-adjusted (factor ${factor}), not identical line items`,
                    }
                    : c);
        }
    }
    return checklists;
}

export function computeOpportunity(opportunity: Opportunity, overrides: BriefingOverrides): ComputedOpportunity {
    const fixed = overrides.fixApplied && opportunity.id === FIX_OPPORTUNITY_ID;
    const compute = COMPUTE_BY_TYPE[opportunity.opportunity_type] ?? baseCompute;
    const result = compute(opportunity, overrides);

    // Deterministic scoring: class confidence = 100 − Σ deductions of the
    // evaluated checklist (falls back to the fixture number when no checklist).
    const checklists = opportunityChecklists(opportunity, overrides);
    const dimensionConfidences = Object.fromEntries(CONFIDENCE_DIMENSIONS.map(cls => [
        cls,
        checklists[cls].length > 0 ? checklistConfidence(checklists[cls]) : opportunity.confidence[cls].score,
    ])) as Record<ConfidenceDimension, number>;

    // A confirmed roll-up change moves the base spend, so both renderings of it follow.
    const restateSpend = (text: string) =>
        fixed ? text.replace('€11.8m', formatCompactEuro(FIX_DELTA.baseSpend)) : text;

    return {
        opportunity,
        appliesTo: restateSpend(opportunity.applies_to ?? ''),
        dimensionConfidences,
        checklists,
        confidence: Math.min(...Object.values(dimensionConfidences)),
        timingLabel: result.timingLabel ?? opportunity.timing.label,
        facts: result.facts,
        derivationSteps: result.derivationSteps,
        costPerWeek: result.costPerWeek,
        costDisplay: result.costDisplay,
        costPlainLanguage: opportunity.cost_of_waiting.plain_language,
        bufferDays: result.bufferDays,
        impact: result.impact,
        impactType: opportunity.potential_impact.impact_type,
    };
}

export type BriefingView = {
    readyToAct: ComputedOpportunity[]
    needsConfirmation: ComputedOpportunity[]
    ambientSentence: string
    urgentCount: number
    counters: Array<{id: string, label: string}>
    savingsTotal: number
    cashTotal: number
    initiativesActive: number
    emptyState: string | null
    createdInitiatives: ComputedOpportunity[]
}

/** The ambient line's second sentence is fixture narration and does not recalculate. */
function ambientTail(): string {
    const text = BRIEFING.ambient_line.text;
    const i = text.indexOf('. ');
    return i >= 0 ? text.slice(i + 2) : '';
}

export function computeView(overrides: BriefingOverrides): BriefingView {
    const computed = BRIEFING.opportunities
        .filter(c => c.state === 'ready_to_act')
        .map(c => computeOpportunity(c, overrides));

    const createdInitiatives = computed.filter(c => overrides.createdInitiatives.includes(c.opportunity.id));
    const eligible = computed.filter(c => !overrides.dismissed[c.opportunity.id] && !overrides.createdInitiatives.includes(c.opportunity.id));

    // Ready to act is a pass/fail gate on the COMPUTED confidence: an edit that
    // drops a dimension below the threshold makes the opportunity need confirmation,
    // and the reverse restores it. Ordering stays a separate layer (cost of waiting).
    const threshold = BRIEFING.ready_to_act_gate.confidence_threshold;
    const readyToAct = eligible.filter(c => c.confidence >= threshold).sort((a, b) => b.costPerWeek - a.costPerWeek);
    const needsConfirmation = eligible.filter(c => c.confidence < threshold);

    const urgentCount = readyToAct.filter(c => c.bufferDays !== undefined && c.bufferDays <= 7).length;
    const opportunities = readyToAct.length;
    const ambientSentence =
        `${opportunities} opportunit${opportunities === 1 ? 'y' : 'ies'} today, ` +
        `${urgentCount} need${urgentCount === 1 ? 's' : ''} action this week. ` +
        ambientTail();

    const savingsTotal = readyToAct.filter(c => c.impactType === 'savings').reduce((acc, c) => acc + c.impact, 0);
    const cashTotal = readyToAct.filter(c => c.impactType === 'cash_release').reduce((acc, c) => acc + c.impact, 0);
    const initiativesActive = BRIEFING.initiatives.initiatives_active + overrides.createdInitiatives.length;
    const needsConfirmationCount = BRIEFING.needs_confirmation.length + needsConfirmation.length;

    const counters = [
        {id: 'opportunities', label: `${opportunities} opportunit${opportunities === 1 ? 'y' : 'ies'} ready to act`},
        {id: 'savings', label: `${formatCompactEuro(savingsTotal)} savings identified`},
        {id: 'cash', label: `${formatCompactEuro(cashTotal)} cash release identified`},
        {id: 'initiatives', label: `${initiativesActive} initiatives, ${BRIEFING.initiatives.initiatives_at_risk} at risk`},
        {id: 'waiting', label: `${BRIEFING.initiatives.tasks_waiting.length} things waiting on you`},
        {
            id: 'needs_confirmation',
            label: needsConfirmationCount === 1
                ? '1 opportunity needs confirmation'
                : `${needsConfirmationCount} opportunities need confirmation`,
        },
    ];

    return {
        readyToAct,
        needsConfirmation,
        ambientSentence,
        urgentCount,
        counters,
        savingsTotal,
        cashTotal,
        initiativesActive,
        emptyState: readyToAct.length === 0 ? BRIEFING.opportunity_list.refill_policy.empty_state : null,
        createdInitiatives,
    };
}

/** Short qualifier shown next to the cost-of-waiting figure. */
export function costOfWaitingTag(opportunity: Opportunity): string {
    if (opportunity.cost_of_waiting.accelerating) return 'accelerating';
    if (opportunity.cost_of_waiting.flagged_slow) return 'slow';
    if (opportunity.cost_of_waiting.plain_language.startsWith('Certain')) return 'certain';
    return 'steady';
}

/** Confidence badge display for a computed opportunity (fixture display until recalculated). */
export function confidenceDisplay(computed: ComputedOpportunity): string {
    const fixtureBadge = confidenceBadge(computed.opportunity);
    if (fixtureBadge && fixtureBadge.value === computed.confidence) return fixtureBadge.display;
    return `${computed.confidence}%`;
}

/**
 * The visible confidence state. A percentage alone is not a business statement:
 * the product leads with the state and keeps the number for calculation and
 * gating. Bands are anchored on the readiness gate (70).
 */
export type ConfidenceState = 'Strong' | 'Supported' | 'Needs confirmation' | 'Insufficient';

export function confidenceState(value: number): ConfidenceState {
    const threshold = BRIEFING.ready_to_act_gate.confidence_threshold;
    if (value >= 90) return 'Strong';
    if (value >= 80) return 'Supported';
    if (value >= threshold) return 'Needs confirmation';
    return 'Insufficient';
}

/** The dimension that sets the opportunity's confidence under the weakest-link rule. */
export function weakestDimension(computed: ComputedOpportunity): ConfidenceDimension {
    return CONFIDENCE_DIMENSIONS.reduce((weakest, cls) =>
        computed.dimensionConfidences[cls] < computed.dimensionConfidences[weakest] ? cls : weakest, CONFIDENCE_DIMENSIONS[0]);
}

/** Human label of a confidence dimension, straight from the fixture's model. */
export function dimensionLabel(cls: ConfidenceDimension): string {
    return BRIEFING.confidence_model.dimensions.find(c => c.id === cls)?.label ?? cls;
}

/** Ready-to-act status: the gate tests impact, confidence and fit — never decision owners. */
export function readyToActLabel(computed: ComputedOpportunity): string {
    const threshold = BRIEFING.ready_to_act_gate.confidence_threshold;
    return computed.confidence >= threshold ? 'ready to act' : 'needs confirmation';
}

/** The classes are weak enough that four separate trips would be wasteful (fixture batch_alternative). */
export function offerFoundationWorklist(computed: ComputedOpportunity): boolean {
    const values = Object.values(computed.dimensionConfidences);
    const threshold = BRIEFING.ready_to_act_gate.confidence_threshold;
    return values.filter(v => v < 80).length >= 2 || values.some(v => v < threshold);
}

// ---------------------------------------------------------------------------
// Diff banner for assumption edits: show the blast radius (BUILD_3 propagation
// table) — the assumption, the owning opportunity's numbers, the totals, the order.
// ---------------------------------------------------------------------------
export function buildAssumptionDiff(
    id: string,
    oldValue: number,
    newValue: number,
    before: BriefingView,
    after: BriefingView,
): string {
    const found = findAssumption(id);
    if (!found) return '';
    const threshold = BRIEFING.ready_to_act_gate.confidence_threshold;
    const typeLabel = (t: string) => BRIEFING.impact_discipline.types.find(v => v.id === t)?.label ?? t;

    const pick = (view: BriefingView) => [...view.readyToAct, ...view.needsConfirmation].find(c => c.opportunity.id === found.opportunityId);
    const b = pick(before);
    const a = pick(after);

    const parts: string[] = [
        `${found.assumption.label} ${formatAssumptionValue(id, oldValue)} -> ${formatAssumptionValue(id, newValue)}.`,
    ];
    if (b && a) {
        if (a.impact !== b.impact) parts.push(`${typeLabel(a.impactType)} ${formatCompactEuro(b.impact)} -> ${formatCompactEuro(a.impact)}.`);
        else parts.push(`${typeLabel(a.impactType)} unchanged at ${formatCompactEuro(a.impact)}.`);
        if (a.confidence !== b.confidence) parts.push(`Confidence ${b.confidence}% -> ${a.confidence}%.`);
        if (a.costPerWeek !== b.costPerWeek) parts.push(`Cost of waiting ${b.costDisplay} -> ${a.costDisplay}.`);
    }
    if (after.savingsTotal !== before.savingsTotal) {
        parts.push(`Savings total ${formatCompactEuro(before.savingsTotal)} -> ${formatCompactEuro(after.savingsTotal)}.`);
    }
    if (after.cashTotal !== before.cashTotal) {
        parts.push(`Cash release total ${formatCompactEuro(before.cashTotal)} -> ${formatCompactEuro(after.cashTotal)}.`);
    }

    const beforeIds = before.readyToAct.map(c => c.opportunity.id);
    const afterIds = after.readyToAct.map(c => c.opportunity.id);
    const wasIn = b && beforeIds.includes(found.opportunityId);
    const isIn = a && afterIds.includes(found.opportunityId);
    if (wasIn && !isIn && a) {
        parts.push(`${a.opportunity.subject} now needs confirmation — ${a.confidence}% is below the ${threshold} gate.`);
    } else if (!wasIn && isIn && a) {
        parts.push(`${a.opportunity.subject} is ready to act again — ${a.confidence}% clears the ${threshold} gate.`);
    } else if (beforeIds.join() !== afterIds.join()) {
        parts.push(`Order changed: ${after.readyToAct.map(c => c.opportunity.subject).join(' -> ')}.`);
    } else {
        parts.push('Order unchanged.');
    }
    return parts.join(' ');
}
